import { useQuery } from "@tanstack/react-query"
import { type ReactNode, useState } from "react"
import { api, type HealthSnapshot, type ProviderHealth } from "@/api.js"
import { Async } from "@/components/Async.js"
import { PageHeader, Section, Stat, StatRow } from "@/components/Page.js"
import { Accordion } from "@/components/ui/accordion.js"
import { TooltipProvider } from "@/components/ui/tooltip.js"
import { plural } from "@/lib/plural.js"
import { cn } from "@/lib/utils.js"
import { SEVERITY, SourceCard, statusLabel } from "./health/Source.js"

/**
 * The screen somebody opens when asked "why is everything on prepayment
 * today". It answers in three steps and shows nothing else:
 *
 *   1. **The verdict**, in one line: what is wrong, or that nothing is.
 *   2. **Every source, worst first** — state, its own one-line summary, and
 *      the one or two figures its grade turns on.
 *   3. **Its full report**, behind a disclosure, one source at a time.
 *
 * Two earlier attempts are in CLAUDE.md and both failed the same way, in
 * opposite directions: a flat wall of twenty equal figures, then the same wall
 * written out as explanatory prose. **What is on the screen is data**; the
 * sentence behind a number is one hover away, under a dotted rule.
 *
 * Nothing here knows what a provider is. Each source reports its own state and
 * this arranges what it is told.
 */
export function HealthScreen() {
  const state = useQuery({ queryKey: ["health"], queryFn: api.health, refetchInterval: 30_000 })

  return (
    <TooltipProvider delayDuration={150}>
      <PageHeader title="Stan systemu" />
      <Async state={state}>{(health) => <Report health={health} />}</Async>
    </TooltipProvider>
  )
}

function Report({ health }: { health: HealthSnapshot }) {
  /** Worst first: the source somebody arrived for is the one at the top. */
  const sources = [...health.providers].sort(
    (left, right) => SEVERITY[left.status] - SEVERITY[right.status],
  )

  /**
   * Opens on the source that needs attention, because that is the one
   * somebody arrived for. Once a person opens or closes one the choice is
   * theirs and survives the thirty-second refetch — a panel that rearranges
   * itself under the cursor is worse than one showing the wrong source.
   */
  const [chosen, setChosen] = useState<string | null>(null)
  const attention = sources.find((source) => source.status !== "ok")?.providerId ?? ""

  return (
    <div className="space-y-9">
      <Verdict sources={sources} />

      <Section title={`Decyzje (${health.decisions.window})`}>
        <StatRow>
          <Stat label="Wszystkie" value={health.decisions.total} />
          <Stat
            label="Przedpłata"
            value={share(health.decisions.prepayment, health.decisions.total)}
            tone="prepay"
          />
          <Stat
            label="Niekompletne dane"
            value={share(health.decisions.degraded, health.decisions.total)}
            tone={health.decisions.degraded > 0 ? "critical" : "neutral"}
          />
        </StatRow>
      </Section>

      <Section title="Źródła danych">
        {sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nie zainstalowano żadnego źródła.</p>
        ) : (
          <Accordion
            type="single"
            collapsible
            value={chosen ?? attention}
            onValueChange={setChosen}
            className="space-y-3"
          >
            {sources.map((source) => (
              <SourceCard key={source.providerId} provider={source} />
            ))}
          </Accordion>
        )}
      </Section>
    </div>
  )
}

/**
 * The answer, in one line, before any figure.
 *
 * **It names the source rather than counting sources** while there is one to
 * name: "1 źródło nie działa" sends somebody looking for which. A second line
 * appears only when there is more than one, and it is a list of names, not a
 * sentence — what this costs is the scoreboard's job, right below.
 */
export function Verdict({ sources }: { sources: readonly ProviderHealth[] }) {
  const broken = sources.filter((source) => source.status === "down" || source.status === "unknown")
  const weak = sources.filter((source) => source.status === "degraded")
  const { tone, headline, listed } = describe(sources, broken, weak)

  const bars = {
    critical: "border-critical",
    prepay: "border-prepay",
    allow: "border-allow",
  } as const

  return (
    <div className={cn("border-l-[3px] py-0.5 pl-4", bars[tone])}>
      <p className="text-[19px] font-semibold tracking-tight text-foreground">{headline}</p>
      {listed !== null && <p className="mt-0.5 text-[13px] text-muted-foreground">{listed}</p>}
    </div>
  )
}

function describe(
  sources: readonly ProviderHealth[],
  broken: readonly ProviderHealth[],
  weak: readonly ProviderHealth[],
): { tone: "critical" | "prepay" | "allow"; headline: string; listed: string | null } {
  if (sources.length === 0) {
    return { tone: "critical", headline: "Brak źródeł danych", listed: null }
  }

  const first = broken[0]
  if (broken.length === 1 && first !== undefined) {
    return {
      tone: "critical",
      headline: `${first.title} — ${statusLabel(first.status)}`,
      listed: null,
    }
  }

  if (broken.length > 1) {
    return {
      tone: "critical",
      headline: `${broken.length} ${plural(broken.length, "źródło", "źródła", "źródeł")} nie ${plural(broken.length, "działa", "działają", "działa")}`,
      listed: names(broken),
    }
  }

  const weakest = weak[0]
  if (weak.length === 1 && weakest !== undefined) {
    return { tone: "prepay", headline: `${weakest.title} — słabnie`, listed: null }
  }

  if (weak.length > 1) {
    return {
      tone: "prepay",
      headline: `${weak.length} ${plural(weak.length, "źródło", "źródła", "źródeł")} ${plural(weak.length, "słabnie", "słabną", "słabnie")}`,
      listed: names(weak),
    }
  }

  return { tone: "allow", headline: "Wszystkie źródła sprawne", listed: null }
}

function names(sources: readonly ProviderHealth[]): string {
  return sources.map((source) => source.title).join(", ")
}

/** A count with its share beside it: the number is the fact, the share is scale. */
function share(count: number, total: number): ReactNode {
  if (total === 0) return "—"
  return (
    <>
      {count}{" "}
      <span className="text-[15px] font-normal text-muted-foreground">
        {Math.round((count / total) * 100)}%
      </span>
    </>
  )
}
